// src/schedule/PortalDropdown.jsx
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

function useIsDayTheme() {
  const get = () => {
    if (typeof document === "undefined") return false;
    const html = document.documentElement;
    return html.getAttribute("data-theme") === "day" && !html.classList.contains("dark");
  };
  const [isDay, setIsDay] = useState(get);
  useEffect(() => {
    if (typeof MutationObserver === "undefined") return;
    const html = document.documentElement;
    const obs = new MutationObserver(() => setIsDay(get()));
    obs.observe(html, { attributes: true, attributeFilter: ["class", "data-theme"] });
    return () => obs.disconnect();
  }, []);
  return isDay;
}

/**
 * Portal do popupów/list/menów.
 * DARK: zostawiamy Twoje stare klasy i wygląd.
 * DAY: tylko nadpisujemy kolory przez var(--panel-2/--text/--border).
 */
export default function PortalDropdown({
  anchorRef,
  open,
  onClose,
  children,
  align = "left",
  minWidth = 280,
  maxWidth = 480,
  maxHeight = 360,
  offset = 8,
  zIndex = 2147483648, // Wyższy niż powiększona karta
}) {
  const isDay = useIsDayTheme();

  const portalRoot = useMemo(() => {
    // Zawsze renderuj do document.body dla wyższego z-index i uniknięcia problemów ze stacking context
    return document.body;
  }, []);

  const panelRef = useRef(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  const [style, setStyle] = useState({ visibility: "hidden" });
  const clamp = (v, mi, ma) => Math.min(Math.max(v, mi), ma);

  const positionPanel = useCallback(() => {
    const anchor = anchorRef?.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;

    panel.style.width = "auto";
    panel.style.maxWidth = `${maxWidth}px`;

    const ar = anchor.getBoundingClientRect();
    let pr = panel.getBoundingClientRect();

    // Jeśli panel nie ma jeszcze szerokości, użyj minWidth
    let panelW = pr.width > 0 ? pr.width : minWidth;
    panelW = clamp(
      panelW,
      minWidth,
      Math.min(maxWidth, window.innerWidth - 16)
    );

    let left;
    if (align === "right") {
      // Wyrównaj prawą krawędź panelu do prawej krawędzi przycisku
      left = ar.right - panelW;
      // Dla align="right" nie ograniczamy maksimum, tylko minimum (aby panel nie wyszedł poza lewą krawędź)
      left = Math.max(left, 8);
    } else if (align === "center") left = ar.left + (ar.width - panelW) / 2;
    else if (align === "auto") left = Math.min(ar.left, window.innerWidth - panelW - 8);
    else left = ar.left;

    // Dla innych align używamy clamp
    if (align !== "right") {
      left = clamp(left, 8, window.innerWidth - panelW - 8);
    }

    setStyle((prev) => ({
      ...prev,
      position: "fixed",
      left: `${left}px`,
      width: `${panelW}px`,
      maxWidth: `min(${maxWidth}px, 96vw)`,
      visibility: "hidden",
    }));

    requestAnimationFrame(() => {
      // Ponownie oblicz pozycję po renderowaniu, aby upewnić się, że jest poprawna
      const anchor = anchorRef?.current;
      const panel = panelRef.current;
      if (!anchor || !panel) return;
      
      const ar = anchor.getBoundingClientRect();
      const pr = panel.getBoundingClientRect();
      
      // Ponownie oblicz szerokość i pozycję
      let panelW = pr.width > 0 ? pr.width : minWidth;
      panelW = clamp(
        panelW,
        minWidth,
        Math.min(maxWidth, window.innerWidth - 16)
      );
      
      let finalLeft;
      if (align === "right") {
        // Wyrównaj prawą krawędź panelu do prawej krawędzi przycisku
        finalLeft = ar.right - panelW;
        finalLeft = Math.max(finalLeft, 8);
      } else if (align === "center") {
        finalLeft = ar.left + (ar.width - panelW) / 2;
      } else if (align === "auto") {
        finalLeft = Math.min(ar.left, window.innerWidth - panelW - 8);
      } else {
        finalLeft = ar.left;
      }
      
      if (align !== "right") {
        finalLeft = clamp(finalLeft, 8, window.innerWidth - panelW - 8);
      }
      
      const ph = panel.getBoundingClientRect().height || 0;
      const topUp = ar.top - ph - offset;
      const top =
        topUp >= 8
          ? topUp
          : clamp(ar.bottom + offset, 8, Math.max(8, window.innerHeight - ph - 8));
      setStyle((s) => ({ 
        ...s, 
        left: `${finalLeft}px`,
        width: `${panelW}px`,
        top: `${top}px`, 
        visibility: "visible" 
      }));
    });
  }, [anchorRef, align, minWidth, maxWidth, offset]);

  useLayoutEffect(() => { if (open) positionPanel(); }, [open, positionPanel]);

  useEffect(() => {
    if (!open) return;
    const reflow = () => positionPanel();
    const onKey = (e) => e.key === "Escape" && onCloseRef.current?.();
    const onDocClick = (e) => {
      const p = panelRef.current;
      const a = anchorRef?.current;
      if (!p) return;
      if (p.contains(e.target)) return;
      if (a && a.contains(e.target)) return;
      onCloseRef.current?.();
    };

    window.addEventListener("resize", reflow);
    window.addEventListener("scroll", reflow, true);
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDocClick);

    return () => {
      window.removeEventListener("resize", reflow);
      window.removeEventListener("scroll", reflow, true);
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDocClick);
    };
  }, [open, positionPanel, anchorRef]);

  if (!open) return null;

  const wrapMaxH =
    typeof maxHeight === "number" ? `${maxHeight}px` : String(maxHeight);

  // Użyj maksymalnego z-index dla portalu w trybie expanded
  const finalZIndex = typeof document !== 'undefined' && document.querySelector('.day-card-expanded-portal') 
    ? 2147483649  // Wyższy niż powiększona karta (2147483647) i inne panele (2147483648)
    : zIndex;
  
  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: finalZIndex, pointerEvents: "none" }}>
      {/* DARK = jak było; DAY = var(--*) */}
        <div
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          ...(isDay
            ? {
              ...style,
              backgroundColor: "var(--panel-2)",
              color: "var(--text)",
              borderColor: "var(--border)",
            }
            : style),
          zIndex: finalZIndex
        }}
        className={
          isDay
            ? "pointer-events-auto rounded-xl shadow-2xl overflow-hidden border"
            : "pointer-events-auto rounded-xl shadow-2xl overflow-hidden bg-gray-900 border border-gray-700 text-gray-200"
        }
      >
        <style>{`
          .no-scrollbar::-webkit-scrollbar { display: none; }
          .no-scrollbar { scrollbar-width: none; -ms-overflow-style: none; }
        `}</style>
        <div
          className="no-scrollbar w-full"
          style={{ maxHeight: wrapMaxH, overflowY: "auto" }}
        >
          {children}
        </div>
      </div>
    </div>,
    portalRoot
  );
}
